import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import {DownIcon, EditIcon, EventIcon, FilterIcon, HistoryIcon, HorizontalMoreIcon, PausedIcon, PlayIcon, PlusIcon, TrashIcon, TrendingUpIcon, UpIcon} from '../../icons'
import {ImgButton, SimpleBlueButton} from '../../components/core/button'
import {useEffect, useRef, useState} from 'react'
import Modal from '../../components/core/modal'
import JobModal from './JobModal'
import {useDispatch, useSelector} from 'react-redux'
import {deleteJob, getJobs, updateJob} from '../../stores/heartbeats'
import {setConfirmIsActive} from '../../stores/alert'
import Table from '../../components/core/table'
import Text from '../../utils/text'
import ContentLoader from '../../components/core/loader'
import EmptyContent from '../resources/EmptyContent'
import Footer from '../../components/layouts/footer'
import Confirm from '../../components/core/confirm'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import {Link} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import {routes} from '../../routes'
import CustomSelect from '../../components/core/customSelect'
import {setTeams} from '../../stores/teams'
import TeamSwitcher from '../../components/pages/teams'
import Customer from '../../utils/customer'
import {colors} from '../../utils/colors'
import classNames from 'classnames'
import {sortBySelected} from '../../utils/sorting'
import Badge from '../../components/core/badge'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import limitUsageManager from '../../utils/hooks/limitUsageManager'
import {getAllLimitUsages} from '../../stores/auth'
import Tooltip from '../../components/core/tooltip'
import ResourceState from '../../components/pages/resource/ResourceState'
import IncidentsModal from '../../components/pages/ResourceIncidents'
import limitManager from '../../utils/limitManager'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const SORT_ID = 'jobs'

const {liveUpdateTime, isReadOnlyModeEnabled} = envConfig

const customFieldLogic = text => ({
    enabled: (item) => item?.enabled ? text.enabled : text.disabled
})

export const getTooltipText = (isLimitExceeded, heartbeat, exceededTitle = '', text) => {
    if (isLimitExceeded) {
        return Text.replaceAllChars(text.status_disabled_tooltip_text_heartbeat,  ':limit_title', exceededTitle?.toLowerCase())
    }

    if (heartbeat?.settings?.interval && !heartbeat?.enabled) {
        return Text.replaceAllChars(text.status_disabled_tooltip_text_interval, ':limit_title', 'incident ruleset')
    }
    return null
}

const Jobs = () => {
    const [jobModal, setJobModal] = useState(false)
    const text = useNamespaceTranslations('jobs')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const [jobs, setJobs] = useState([])
    const [sortedJobs, setSortedJobs] = useState([])
    const [filteredJobs, setFilteredJobs] = useState([])
    const [selectedJobs, setSelectedJobs] = useState([])
    const [isFetching, setIsFetching] = useState(true)
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [activeIncidentModalId, setActiveIncidentModalId] = useState('')
    const [clickedJob, setClickedJob] = useState(null)
    const services = useSelector(state => state.resources.services)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const selectedTeamId = useSelector(state => state.teams.selectedTeamId)
    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)
    const dispatch = useDispatch()
    const dropdownMenuRef = useRef()
    const limitUsages = {
        heartbeat: limitUsageManager(usageLimitsData.limit_modules.monitoring.module, usageLimitsData.limit_keys.heartbeat_count.limit_key, organisationLimitUsages),
        interval: limitUsageManager(usageLimitsData.limit_modules.incident_management.module, usageLimitsData.limit_keys.heartbeat_incident_ruleset_count.limit_key, organisationLimitUsages)
    }

    const isLimitExceeded = limitUsages.heartbeat.isLimitExceeded
    const isIntervalLimitExceeded = limitUsages.interval.isLimitExceeded

    const isHeartbeatMonitoringDisabled =  !limitManager.getHeartbeatMonitoringIsEnabled(usageLimitsData)

    const initialFilter = {
        service_id: services
    }
    const [filter, setFilter] = useState(initialFilter)

    const ActionButtons = ({data}) => {
        const tooltipText = getTooltipText(isLimitExceeded, data, limitUsages.heartbeat.title, text)
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            <MenuItem onClick={() => {
                setClickedJob(data)
                toggleModal(true)
            }
            } disabled={isHeartbeatMonitoringDisabled || isReadOnlyModeEnabled}>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
            {!data?.enabled && (isLimitExceeded || (isIntervalLimitExceeded  && data?.settings?.interval)) ?
                <Tooltip content={tooltipText} trigger='mouseenter' placement='top'>
                    <MenuItem onClick={() => handleChangeStatus({...data, enabled: !data?.enabled})} disabled={true}>
                        <span className="d-flex">{<PlayIcon />}</span>
                        <span>{text.enable}</span>
                    </MenuItem>
                </Tooltip> :
                <MenuItem onClick={() => handleChangeStatus({...data, enabled: !data?.enabled})} disabled={isHeartbeatMonitoringDisabled || isReadOnlyModeEnabled}>
                    <span className="d-flex">{data?.enabled ? <PausedIcon /> : <PlayIcon />}</span>
                    <span>{data?.enabled ? text.disable : text.enable}</span>
                </MenuItem>}
            <MenuDivider/>
            <MenuItem className="danger" onClick={() => {
                setClickedJob(data)
                dispatch(setConfirmIsActive(true))
            }
            } disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    const tableHeader = [
        {
            label: text.name,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: text.service_name,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'service_name_asc',
                    field: ['service_id']
                },
                desc: {
                    key: 'service_name_desc',
                    field: ['service_id']
                }
            }
        },
        {
            label: text.state,
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'interval_asc',
                    field: ['interval']
                },
                desc: {
                    key: 'interval_desc',
                    field: ['interval']
                }
            }
        },
        {
            label: text.methods,
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'methods_asc',
                    field: ['methods']
                },
                desc: {
                    key: 'methods_desc',
                    field: ['methods']
                }
            }
        },
        {
            label: text.status,
            className: 'min-width-250',
            sorting: {
                asc: {
                    key: 'enabled_asc',
                    field: ['enabled']
                },
                desc: {
                    key: 'enabled_desc',
                    field: ['enabled']
                }
            }
        }
    ]

    const handleActiveIncidentModal = (id) => {
        setActiveIncidentModalId(activeIncidentModalId === id ? '' : id)
    }

    const VisitJob = ({id}) => {
        return (
            <>
                <Tooltip content={text.view_statistics} placement='bottom' trigger='mouseenter'>
                    <span className="button-field">
                        <Link
                            to={NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.job.paths), ':id', id)}
                        >
                            <TrendingUpIcon/>
                        </Link>
                    </span>
                </Tooltip>
                <Tooltip content={text.view_events} placement='bottom' trigger='mouseenter'>
                    <span className="button-field">
                        <Link
                            to={`${NavigationHelper.getLocalizedPath(routes.job_events.paths)}?id=${id}&&team_id=${selectedTeamId}`}
                            target='_blank'
                        >
                            <span className='event-icon'><EventIcon fill={colors.colorMuted}/></span>
                        </Link>
                    </span>
                </Tooltip>
                <Tooltip content={text.incidents} placement='bottom' trigger='mouseenter'>
                    <span className={classNames('table-action-btn', {
                        dark: isDarkMode
                    })} onClick={() => handleActiveIncidentModal(id)}>
                        <HistoryIcon fill={isDarkMode ? colors.colorFilterTitle : colors.colorMuted}/>
                    </span>
                </Tooltip>
            </>
        )
    }

    const tbodyData = sortedJobs?.map(item => {
        return [
            <span className={classNames('min-width', {
                'disabled-interval': !item?.settings?.interval
            })}>
                <Link
                    className="underlined"
                    to={NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.job.paths), ':id', item?.heartbeat_id)}>{item?.name}</Link>
            </span>,
            <span className="min-width">{services?.find(service => service?.value === item?.service_id)?.label}</span>,
            <span className="min-width-150">
                <span className='state-icon'>
                    <ResourceState
                        tooltipText={
                            !item?.settings?.interval
                                ? text.warning_text
                                : item?.has_open_incidents
                                    ? text.down
                                    : text.up
                        }
                        icon={item?.settings?.interval && (item?.has_open_incidents ? <DownIcon /> : <UpIcon />)}
                        placement='bottom'
                    />
                </span>
            </span>,
            <span className="min-width-150">{item?.methods.join(', ')}</span>,
            <span className="d-flex justify-content-between min-width-250">
                <Badge className="lowercase" color={item?.enabled ? text.success_status : text.warning_status}>
                    {item?.enabled ? text.enabled_status : text.disabled_status}
                </Badge>
                <span className="d-flex align-items-center gap-half">
                    <VisitJob id={item.heartbeat_id}/>
                    {Customer.isOwnTeam() && <ActionButtons data={item}/>}
                </span>
            </span>
        ]
    })

    const toggleModal = (isEdit = false) => {
        setJobModal({
            isEdit
        })
    }

    const closeModal = () => {
        setJobModal(false)
        fetchData(false)
        setClickedJob(null)
    }

    const deleteHeartbeats = async() => {
        setIsDeleting(true)
        await dispatch(deleteJob(clickedJob ? [clickedJob?.heartbeat_id] : selectedJobs))
        setIsDeleting(false)
        dispatch(setConfirmIsActive(false))
        fetchData(false)
        setSelectedJobs([])
    }

    const fetchData = async(showLoader = true) => {
        setIsFetching(showLoader)
        const data = await dispatch(getJobs())
        setJobs(data)
        setFilteredJobs(data)
        setSortedJobs(data)
        setIsFetching(false)
    }

    const handleChangeFilter = (name, value) => {
        setFilter(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleChangeStatus = async(payload) => {
        setIsButtonLoading(true)
        await dispatch(updateJob(payload?.heartbeat_id, {...payload, interval: payload?.settings?.interval}))
        setIsButtonLoading(false)
        await fetchData(false)
    }

    const searchByFilter = async(reset = false) => {
        dropdownMenuRef?.current?.closeMenu()
        if (reset) {
            setFilter(initialFilter)
            setFilteredJobs(jobs)
            return
        }
        const serviceId = filter.service_id.map(item => item.value)

        const data = jobs?.filter(item => serviceId.includes(item.service_id))
        setFilteredJobs(data)
    }

    useEffect(() => {
        setSortedJobs(sortBySelected(filteredJobs, SORT_ID, tableHeader, customFieldLogic(text)))
    }, [filteredJobs])

    useEffect(() => {
        if (services?.length) {
            fetchData()
            setFilter(prev => ({
                ...prev,
                service_id: services
            }))
            dispatch(getAllLimitUsages())
            setSelectedJobs([])
        }
    }, [services])

    useEffect(() => {
        dispatch(setTeams([]))
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            fetchData(false)
        }, liveUpdateTime)

        return () => clearInterval(interval)
    }, [])

    const isDisabled = selectedJobs?.length !== 1 || isHeartbeatMonitoringDisabled
    const selectedJob = jobs?.find(item => item?.heartbeat_id === selectedJobs?.[0])
    const tooltipText = getTooltipText(isLimitExceeded, selectedJob, limitUsages.heartbeat.title, text)
    const activeJob = jobs?.find(item => item?.heartbeat_id === activeIncidentModalId)

    return (
        <main className={classNames('main jobs', {
            dark: isDarkMode
        })}>
            {activeIncidentModalId && <Modal
                isOpen={activeIncidentModalId}
                contentClassName="overflow-auto"
                id="large-modal"
                title={text.incidents}
                closeModal={() => handleActiveIncidentModal(activeIncidentModalId)}
            >
                <span className='incident-modal-sub-title'>
                    <Tooltip content={text.view_statistics}>
                        <Link
                            to={NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.job.paths), ':id', activeIncidentModalId)}
                            className={classNames({
                                'table-action-btn': true,
                                dark: isDarkMode
                            })}
                        >
                            <TrendingUpIcon/>
                        </Link>
                    </Tooltip>
                    <p className='incident-modal-sub-title-text'>{activeJob?.name}</p>
                </span>
                <IncidentsModal
                    resourceId={activeIncidentModalId}
                    resourceType='Heartbeat'
                />
            </Modal>}
            <Modal
                closeModal={() => {
                    setJobModal(false)
                    setClickedJob(null)
                }}
                contentClassName='resource-modal-content interval-based-ruleset'
                isOpen={Boolean(jobModal)}
                title={jobModal?.isEdit ? text.edit_job : text.add_new_job}
            >
                <JobModal jobsLength={jobs?.length} closeModal={closeModal} modal={jobModal}
                    heartbeat={clickedJob}/>
            </Modal>
            <Confirm
                body={text.are_you_sure}
                callback={deleteHeartbeats} showLoader={isDeleting}/>
            <div className="content-container">
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {Customer.isOwnTeam() && (
                        <ImgButton onClick={() => toggleModal()} disabled={isReadOnlyModeEnabled}>
                            {text.add_job}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    )}
                </div>
                <TeamSwitcher/>
                <div className="search-filter-actions">
                    <Dropdown closeMenu={true}
                        menuButton={
                            <button className="filter-button">
                                <FilterIcon/>
                                {text.filters}
                            </button>
                        } innerRef={dropdownMenuRef} align="start">
                        <CustomSelect
                            options={services}
                            value={filter.service_id}
                            placeholder={text.service_name}
                            name="service_id"
                            isMulti
                            onChange={(name, value) => handleChangeFilter(name, value)}/>
                        <div className="d-flex w-100 justify-content-between gap-1">
                            <SimpleBlueButton className="outline w-100"
                                onClick={() => searchByFilter(true)}>{text.reset}</SimpleBlueButton>

                            <SimpleBlueButton className="w-100"
                                onClick={() => searchByFilter()}>{text.search}</SimpleBlueButton>
                        </div>
                    </Dropdown>
                    {Customer.isOwnTeam() && (
                        <div className="selected-actions">
                            <ImgButton onClick={() => toggleModal()}
                                className="responsive-add-button">
                                <span><PlusIcon/></span>
                            </ImgButton>
                            <ImgButton className={classNames({
                                'light-bg': !isDarkMode
                            })}
                            disabled={isDisabled || !selectedJob?.enabled || isReadOnlyModeEnabled}
                            onClick={() => handleChangeStatus({...selectedJob, enabled: false})}
                            >
                                <span>{(isButtonLoading && selectedJob?.enabled) && !isDisabled ? <ButtonLoader /> : <PausedIcon />}</span>
                                <span className="button-text">{text.disable}</span>
                            </ImgButton>
                            {selectedJob && !selectedJob?.enabled && (isLimitExceeded  || (isIntervalLimitExceeded && selectedJob?.settings?.interval)) ?
                                <Tooltip content={tooltipText} trigger='mouseenter' placement='top'>
                                    <ImgButton
                                        className={classNames({
                                            'light-bg': !isDarkMode
                                        })}
                                        disabled={true}
                                        onClick={() => handleChangeStatus({...selectedJob, enabled: true})}>
                                        <span className="d-flex">{<PlayIcon />}</span>
                                        <span>{text.enable}</span>
                                    </ImgButton>
                                </Tooltip> :
                                <ImgButton className={classNames({
                                    'light-bg': !isDarkMode
                                })}
                                disabled={isDisabled || selectedJob?.enabled || isReadOnlyModeEnabled}
                                onClick={() => handleChangeStatus({...selectedJob, enabled: true})}
                                >
                                    <span>{(isButtonLoading && !selectedJob?.enabled) && !isDisabled ? <ButtonLoader /> : <PlayIcon />}</span>
                                    <span className="button-text">{text.enable}</span>
                                </ImgButton>}
                            <ImgButton className={classNames({
                                'light-bg': !isDarkMode
                            })}  disabled={isDisabled || isReadOnlyModeEnabled}
                            onClick={() => {
                                setClickedJob(selectedJob)
                                toggleModal(true)
                            }}>
                                <span><EditIcon/></span>
                                <span className="button-text">{text.edit}</span>
                            </ImgButton>
                            <ImgButton className={classNames('outline-red', {
                                'light-bg': !isDarkMode
                            })}  disabled={selectedJobs.length === 0 || isReadOnlyModeEnabled}
                            onClick={() => dispatch(setConfirmIsActive(true))}>
                                <span><TrashIcon stroke={colors.colorRed}/></span>
                                <span className="button-text">{text.delete}</span>
                            </ImgButton>
                        </div>
                    )}
                </div>
                {isFetching ? (
                    <ContentLoader/>
                ) : (
                    sortedJobs?.length > 0 ? (
                        <Table
                            tableHeader={tableHeader}
                            tbodyData={tbodyData}
                            identificator='heartbeat_id'
                            hasCheckbox={Customer.isOwnTeam()}
                            strech
                            data={sortedJobs}
                            isMultiCheckable
                            selectedData={selectedJobs}
                            setSelectedData={data => setSelectedJobs(data)}
                            isSortingEnabled
                            setData={setSortedJobs}
                            customFieldLogic={customFieldLogic(text)}
                            filteredData={filteredJobs}
                            sortId={SORT_ID}
                        />
                    ) : (
                        <EmptyContent message={text.no_data} hideButton={true}/>
                    )
                )}
            </div>
            <Footer/>
        </main>
    )
}

export default Jobs
